import { telefonoConPrefijo, telefonoParaEnlace } from '../../lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { AlertTriangle, UserPlus, UserX, CalendarClock, CalendarPlus, CalendarX2, CheckCircle2, Clock, ExternalLink, ListChecks, Loader2, Lock, MapPin, MessageCircle, MoveRight, Pencil, Phone, RotateCcw, Star, StickyNote, Stethoscope, Syringe, User, X } from 'lucide-react';
import { api, formatDate } from '../../lib/api';
import { AVISO_COSTE_RETOQUE, marcarNoAcude, deshacerNoAcude, abrirPestanaYa, avisarCosteRetoque, avisarSaludoNeutro, cancelCita, costeRetoqueDesconocido, esConceptoRetoque, conceptColor, conceptosElegibles, getNotasInternas, llevarPestanaA, mensajeCita, minutosConcepto, nombreCanal, nombreConcepto, setEstadoCita, updateCita, estadosDeLaCita, avisosDelEpisodio, consultaConjuntaDeLaCita, esChalazionDeQuirofano, esConsultaEnQuirofano, ESTADO_RESERVA, colorDeLaReserva, conceptoAdmiteReserva, leerTipoCita, nombreReserva, nombreTipoDeLaCita, opcionesTipoCita, reservaPagada, reservaPendiente, reservaSePagaSola, valorTipoCita } from '../../lib/citasApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';
import { PatientCombobox } from '../PatientCombobox';
import PatientFormDialog, { fichaDesdeNombre } from '../PatientFormDialog';
import MoveCitaDialog from './MoveCitaDialog';
import PasosCirugiaDialog from './PasosCirugiaDialog';
import DarCitaDialog from './DarCitaDialog';
import InfoClinicaPaciente from './InfoClinicaPaciente';
import CitaDuplicadaAviso from './CitaDuplicadaAviso';
import CentrosResena from './CentrosResena';
import EtiquetasIntervenciones, { ordenarIntervenciones } from './EtiquetasIntervenciones';
import EtiquetasMarcasPaciente, { marcasDeLaCita } from './EtiquetasMarcasPaciente';
import { useAuth } from '../../lib/auth';
import { canSeeInternalNotes, isAdmin, puedeExcepcionProhibido } from '../../lib/roles';
import IndicadorProhibido from '../IndicadorProhibido';
import { DialogoPacienteProhibido, esErrorProhibido } from './AvisoPacienteProhibido';

/** "21/07/2026 18:42" a partir de un ISO. */
function formatFechaHora(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${formatDate(iso.slice(0, 10))} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}

/**
 * Los DOS mensajes que se le mandan a una paciente, cada uno con SU casilla en
 * la cita (revisión 2026-09-21). Son mensajes distintos y con semanas de por
 * medio: la confirmación el día en que se le da la cita y el recordatorio el
 * día antes. Hasta hoy los dos botones de aquí llamaban al mismo POST
 * /whatsapp-enviado sin decir cuál era, el servidor marcaba siempre el
 * recordatorio, y a la paciente a la que se le mandó la confirmación al dar la
 * cita se le saltaba después el recordatorio (el bloque la omitía «Ya
 * enviado» y el email automático ni la miraba).
 *
 * El orden es el de la vida de la cita: primero la confirmación.
 */
const MARCAS_MENSAJE = [
  {
    tipo: 'confirmacion',
    campoAt: 'confirmacion_enviada_at',
    campoCanal: 'confirmacion_canal',
    rotulo: 'Confirmación enviada',
    testid: 'cita-detail-confirmacion-enviada',
  },
  {
    tipo: 'recordatorio',
    campoAt: 'recordatorio_enviado_at',
    campoCanal: 'recordatorio_canal',
    rotulo: 'Recordatorio enviado',
    testid: 'cita-detail-recordatorio-enviado',
  },
];
/** Lo que no se reconozca cuenta como recordatorio, igual que en el servidor. */
const marcaDeMensaje = (tipo) => MARCAS_MENSAJE.find((m) => m.tipo === tipo) || MARCAS_MENSAJE[1];

/** Los seis apartados de la guía, con las claves que devuelve
 * /api/cirugia-pasos/estado (mismo orden que ve el paciente). */
const APARTADOS_GUIA = ['instrucciones', 'preoperatorio', 'biometria', 'consentimientos', 'postoperatorio', 'pago'];

/** Texto corto del seguimiento de la guía: cargando, sin poder consultar,
 * sin enlace todavía o «n de 6 apartados» («n de 5» en un retoque: el
 * backend manda su orden en `pasos_orden`, 2026-09-08). */
function PasosResumen({ estado }) {
  if (estado === undefined) return <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />;
  if (estado === null) return <span className="text-xs">ver seguimiento</span>;
  // Anulada o «no acude» (22/9/2026): no hay apartados que contar de una
  // cirugía que no va a ser, y «sin enlace todavía» invitaría a generarlo.
  if (estado.estado === 'cancelada') {
    return (
      <span className="text-xs text-red-700" data-testid="cita-detail-pasos-cerrada">
        {estado.motivo === 'no_acude' ? 'no acudió' : 'cirugía anulada'}
      </span>
    );
  }
  if (!estado.tiene_enlace) return <span className="text-xs text-amber-700">sin enlace todavía</span>;
  const apartados = estado.pasos_orden?.length ? estado.pasos_orden : APARTADOS_GUIA;
  const hechos = apartados.filter((k) => estado.pasos?.[k]?.hecho).length;
  const completa = hechos === apartados.length;
  return (
    <span className={`text-xs font-medium tabular-nums ${completa ? 'text-emerald-700' : 'text-[#13231E]'}`}>
      {hechos} de {apartados.length} apartados
    </span>
  );
}

/** Retoque (dirección, 2026-09-08, punto 8): qué va a pasar con el bloque
 * «Gastos de quirófano» de la confirmación, ANTES de pulsar el botón. El
 * backend busca el coste en la cita, los episodios y la historia
 * (/api/recordatorios/cita/{id}/retoque-coste): sin coste → sin bloque; con
 * coste → bloque íntegro; sin saberse (o sin poder consultar) → el bloque NO
 * va y recepción ve el aviso para añadir el cobro a mano si corresponde.
 *
 * Ese último caso cambió de sentido el 2026-09-19 (administración: «no enviar datos
 * de pago si la cirugía es sin coste»): antes, en la duda, se mandaba el IBAN.
 * Ahora en la duda no se manda nada, porque pedirle dinero a quien no lo debe
 * hace más daño que olvidar cobrarlo, y esto último lo arregla recepción
 * leyendo el aviso. Si el aviso dijera lo de antes, nadie lo arreglaría. */
function RetoqueCoste({ coste }) {
  if (coste === undefined) return null;
  if (coste && coste.es_retoque === false) return null;
  const desconocido = coste === null || coste.con_coste === null;
  if (desconocido) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
        data-testid="cita-detail-retoque-coste"
        data-coste="desconocido"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
        <span>
          <strong>{AVISO_COSTE_RETOQUE}.</strong> La confirmación saldrá SIN el bloque
          «Gastos de quirófano»: si el retoque se cobra, hay que decírselo aparte.
        </span>
      </div>
    );
  }
  const fuente = coste.fuente ? ` (${coste.fuente})` : '';
  // Con coste pero sin la cuenta en Configuración (V2, 28/9/2026): el bloque
  // no puede salir, y se dice en vez de prometerlo.
  if (coste.con_coste && coste.sin_cuenta_de_pago) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
        data-testid="cita-detail-retoque-coste"
        data-coste="sin-cuenta"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
        <span>
          Retoque <strong>con coste</strong>{fuente}, pero la clínica no tiene la cuenta de los pagos en
          Configuración: la confirmación saldrá SIN el bloque «Gastos de quirófano».
        </span>
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-[#D9E3DD] bg-white px-2.5 py-2 text-xs text-muted-foreground"
      data-testid="cita-detail-retoque-coste"
      data-coste={coste.con_coste ? 'con' : 'sin'}
    >
      {coste.con_coste
        ? <>Retoque <strong>con coste</strong>{fuente}: la confirmación lleva el bloque «Gastos de quirófano».</>
        : <>Retoque <strong>sin coste</strong>{fuente}: la confirmación sale sin el bloque «Gastos de quirófano».</>}
      {coste.texto ? <span className="block mt-0.5 italic">«{coste.texto}»</span> : null}
    </div>
  );
}

/**
 * Notas de administración del paciente dentro del resumen de cita (administración,
 * 18/9/2026): las mismas que ya salen en la ficha, aquí debajo de la
 * información clínica y encima del flujo del día, para leerlas sin salir de
 * la agenda.
 *
 * QUIÉN LAS VE NO LO DECIDE ESTA PANTALLA. Dicen lo que debe un paciente o
 * por qué no se le da cita, así que la puerta sigue siendo la del servidor:
 * GET /api/patients/{id}/notas-internas, que las guarda en colección aparte y
 * contesta 403 a quien no debe verlas. Con un 403 aquí no se pinta NADA: ni
 * bloque vacío ni aviso —un aviso ya delataría que hay algo escrito—. El
 * filtro por rol de antes de preguntar (`puedeVerlas`) es solo para no llamar
 * a una puerta que va a dar 403, y para que a enfermería no le salga siquiera
 * el aviso de fallo; la regla la hace cumplir el servidor, no este `if`.
 *
 * Y al revés (regla de la casa, tanda del 18/9): si la petición se cae —no es
 * un 403, es que no se ha podido consultar— se dice. Nunca «sin notas»: eso
 * sería enseñar un error como si fuera un dato, y quien lea el resumen se
 * fiaría de un paciente «sin avisos» que sí los tiene.
 *
 * Mientras carga tampoco hay rótulo ni hueco: un «Cargando notas de
 * administración…» que después desaparece es otra forma de contar lo que no
 * toca.
 */
function NotasAdminCita({ patientId, puedeVerlas, testId = 'cita-detail-notas-admin' }) {
  // Las marcas de paciente ya no van aquí (administración, 21/10/2026): son
  // la etiqueta de su color junto al nombre, arriba del resumen.
  // undefined = cargando · { texto, meta } = notas · { sinPermiso } = 403 ·
  // { error } = no se ha podido consultar.
  const [estado, setEstado] = useState(undefined);

  useEffect(() => {
    if (!patientId || !puedeVerlas) {
      setEstado(undefined);
      return undefined;
    }
    // `vigente` evita pintar las notas de un paciente anterior si se cambia de
    // cita antes de que llegue la respuesta (igual que InfoClinicaPaciente).
    let vigente = true;
    setEstado(undefined);
    getNotasInternas(patientId)
      .then((d) => {
        if (!vigente) return;
        const doc = d && !Array.isArray(d) ? d : {};
        setEstado({ texto: (doc.texto || '').trim(), meta: doc });
      })
      .catch((e) => {
        if (!vigente) return;
        setEstado(e?.response?.status === 403 ? { sinPermiso: true } : { error: true });
      });
    return () => { vigente = false; };
  }, [patientId, puedeVerlas]);

  // Cita sin ficha o rol sin permiso: nada.
  if (!patientId || !puedeVerlas) return null;
  // 403 del servidor o todavía cargando: nada.
  if (estado === undefined || estado.sinPermiso) return null;

  if (estado.error) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        data-testid={`${testId}-error`}
      >
        <AlertTriangle className="w-4 h-4 shrink-0 text-red-600" />
        <span>
          No se han podido cargar las notas de administración. Vuelve a abrir la cita o míralas en la ficha del paciente.
        </span>
      </div>
    );
  }

  const texto = estado.texto;
  // Con permiso y sin nada escrito: no se estorba con un bloque vacío.
  if (!texto) return null;

  const meta = estado.meta || {};
  return (
    <div
      className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.04)] px-3 py-2.5"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        <Lock className="w-3.5 h-3.5 text-[#2E6E5E]" /> Notas de administración
      </div>
      <div className="whitespace-pre-wrap text-sm text-[#13231E]" data-testid={`${testId}-texto`}>
        {texto}
      </div>
      {meta.updated_at && (
        <div className="mt-1.5 text-[11px] text-muted-foreground">
          {`Última edición: ${formatFechaHora(meta.updated_at)}`}
          {meta.updated_by_name ? ` · ${meta.updated_by_name}` : ''}
        </div>
      )}
    </div>
  );
}

/**
 * Detalle de una cita al pulsarla en la rejilla: datos del paciente y acciones
 * (mover, cancelar, ver ficha). Diálogo pequeño para que funcione igual en
 * vista día y semana.
 *
 * Props:
 *  - open / onOpenChange
 *  - cita:      la cita pulsada
 *  - conceptos: catálogo (para el color del concepto)
 *  - onChanged: callback tras mover/cancelar (la página recarga)
 */
export default function CitaDetailPopover({ open, onOpenChange, cita, conceptos = [], agenda = null, agendas = [], onChanged, onCancelada }) {
  // El check de enfermería solo existe en agendas con consulta conjunta
  // (Blefaroplastia: enfermería ve primero al paciente, después el doctor).
  // La de la CITA, no la abierta (24/9/2026): en la agenda de Enfermería las
  // prestadas de Blefaroplastia se quedaban sin el botón que usa enfermería.
  const esQuirofano = agenda?.tipo === 'quirofano';
  // El chalazión citado en quirófano (30/9/2026) recibe los mensajes de una
  // consulta —lo decide el servidor, que marca la cita—: ni guía de «mis
  // pasos» ni su seguimiento, y el rótulo del recordatorio lo dice.
  // Una consulta citada en quirófano (una revisión, 2/10/2026) se comporta en
  // todo como en la agenda de consultas: también lo dice el servidor.
  const consultaEnQuirofano = esQuirofano && esConsultaEnQuirofano(cita);
  const mensajesDeCirugia = esQuirofano && !esChalazionDeQuirofano(cita) && !consultaEnQuirofano;
  // «No acude» (dirección, 2026-09-08, punto 12): la cita sigue en la agenda,
  // tachada, y al pulsarla se abre este resumen. Sin flujo del día, sin visto
  // de enfermería y sin «Mover» (el servidor solo lo admite en citas
  // activas): lo que procede es «Deshacer» si fue un error o «Dar cita».
  const noAcude = cita?.status === 'no_acude';
  const consultaConjunta = !esQuirofano && consultaConjuntaDeLaCita(cita, [agenda, ...agendas]);
  const { user } = useAuth();
  const admin = isAdmin(user);
  // Notas de administración (administración, 18/9/2026): a enfermería, doctores y
  // anestesistas ni se les pregunta —el servidor les daría 403—, así no les
  // sale ni el aviso de fallo. El candado de verdad es el suyo, no este.
  const puedeVerNotasAdmin = canSeeInternalNotes(user);
  const [moveOpen, setMoveOpen] = useState(false);
  const [darCitaOpen, setDarCitaOpen] = useState(false);
  // El paciente PROHIBIDO al que se intentó pasar la cita (14/10/2026).
  const [prohibidoPendiente, setProhibidoPendiente] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // Consulta conjunta: la marca «visto por enfermería» (con quién la puso).
  // Desde el 15/10/2026 se pone con el estado «Visto por enfermería» de la
  // fila de estados; el servidor la devuelve en la cita.
  const [visto, setVisto] = useState(false);
  const [vistoPor, setVistoPor] = useState(null);
  // Flujo del día: espera / enfermeria / visto_enfermeria / tratamiento /
  // realizada en consulta (y los de quirófano).
  const [estado, setEstado] = useState(null);
  const [savingEstado, setSavingEstado] = useState(false);
  // Edición de la nota de cita.
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  // «Tocado»: el aviso rojo de detalles vacíos solo tras salir del campo o
  // intentar guardar, no nada más abrir el editor (revisión 2026-09-03).
  const [notesTocado, setNotesTocado] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  // Cambio del TIPO de cita (concepto) una vez creada. Con la duración
  // recalculada del nuevo tipo y editable antes de guardar, como en los
  // diálogos de alta (revisión 2026-09-03; el requisito «editable» del punto
  // 4 también al editar): `conceptDraft` es el tipo elegido y `durDraft` los
  // minutos (texto del input).
  const [editingConcept, setEditingConcept] = useState(false);
  const [savingConcept, setSavingConcept] = useState(false);
  const [conceptDraft, setConceptDraft] = useState('');
  // «Reserva <tipo>» (12/10/2026): elegir el tipo normal en una reserva sin
  // pagar la quita a mano (estado `confirmada`; 15/10/2026: en cualquier modo,
  // p. ej. se cobró fuera); elegir «Reserva X», la pone.
  const [reservaDraft, setReservaDraft] = useState(false);
  const [durDraft, setDurDraft] = useState('');

  // Cambio de PACIENTE y de PROCEDIMIENTO desde el propio resumen.
  const [editingPatient, setEditingPatient] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  // Alta de contacto desde el propio resumen de la cita (administración,
  // 2026-09-11): el paciente que llama no está en la lista y había que irse a
  // la sección Pacientes y volver.
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  // El formulario se abre con el nombre que YA lleva la cita repartido en
  // nombre y apellidos (las citas traídas de la agenda o del Agente llevan
  // nombre pero no ficha). Memorizado porque PatientFormDialog reinicia el
  // formulario cada vez que cambia `initial`: un objeto nuevo por render
  // borraría lo tecleado.
  const nuevoContactoInicial = useMemo(
    () => (newPatientOpen ? fichaDesdeNombre(cita?.patient_name) : null),
    [newPatientOpen, cita?.patient_name],
  );
  const [editingProc, setEditingProc] = useState(false);
  const [procDraft, setProcDraft] = useState('');
  const [savingProc, setSavingProc] = useState(false);
  const [pasosOpen, setPasosOpen] = useState(false);
  // Seguimiento de la guía de «mis pasos» de una cirugía (2026-09-03, dirección,
  // punto 7): el botón «Guía de mis pasos» desapareció del resumen y su contenido va
  // dentro del Recordatorio, pero a la clínica le sigue haciendo falta VER qué
  // apartados lleva hechos el paciente. `undefined` = cargando, `null` = no se
  // pudo consultar; `pasosTick` fuerza una recarga (tras mandar el recordatorio,
  // que crea el enlace, y al cerrar el detalle).
  const [pasosEstado, setPasosEstado] = useState(undefined);
  const [pasosTick, setPasosTick] = useState(0);
  // Retoque: ¿lleva pago la confirmación? `undefined` = no procede o
  // cargando, `null` = no se pudo consultar (dirección, 2026-09-08, punto 8).
  const [costeRetoque, setCosteRetoque] = useState(undefined);
  const [resenaOpen, setResenaOpen] = useState(false);
  const [enviandoMsg, setEnviandoMsg] = useState('');
  // Envío hecho desde ESTE resumen (cierre 2026-09-05): el POST de
  // whatsapp-enviado no devuelve la cita, y la lista de la agenda se recarga
  // por detrás; con esto «Recordatorio enviado el …» se ve al momento.
  // Una entrada POR MENSAJE desde el 2026-09-21 ({confirmacion, recordatorio}):
  // mandar la confirmación no puede tapar lo que diga el recordatorio, ni al
  // revés, porque son dos avisos distintos.
  const [enviadoAqui, setEnviadoAqui] = useState({});
  // Deshaciendo un «no acude» (dirección, 2026-09-08, punto 12).
  const [deshaciendoNoAcude, setDeshaciendoNoAcude] = useState(false);

  // OJO: depende de `cita?.id`, NO del objeto. Ahora la cita se refresca en el
  // sitio al guardar, y con `cita` entero esto se disparaba en cada guardado y
  // cerraba de golpe el campo que estabas editando (2026-07-28).
  useEffect(() => {
    if (!open) return;
    setMoveOpen(false);
    setCancelOpen(false);
    setCancelReason('');
    setCancelling(false);
    setEditingConcept(false);
    setEditingNotes(false);
    setEditingPatient(false);
    setEditingProc(false);
    setSavingNotes(false);
    // Vacío, no `null`: desde el 2026-09-21 es un mapa {tipo: marca}.
    setEnviadoAqui({});
  }, [open, cita?.id]);

  // Los valores mostrados sí siguen a la cita: si cambia por fuera, se ve.
  useEffect(() => {
    setVisto(!!cita?.visto_enfermeria);
    setVistoPor(cita?.visto_enfermeria_by || null);
    setEstado(cita?.estado_paciente || null);
  }, [cita?.visto_enfermeria, cita?.visto_enfermeria_by, cita?.estado_paciente]);

  useEffect(() => {
    if (!editingNotes) setNotesDraft(cita?.notes || '');
  }, [cita?.notes, editingNotes]);

  useEffect(() => {
    if (!editingProc) setProcDraft(cita?.procedimiento || '');
  }, [cita?.procedimiento, editingProc]);

  // Solo en quirófano y con ficha: la misma consulta que hace el detalle al
  // abrirse. `vigente` evita pintar la respuesta de una cita anterior si se
  // cambia de cita antes de que llegue.
  // Se pregunta por el proceso de ESTA cita (dirección, 2026-09-08): cada
  // cirugía tiene su enlace, y una anterior del paciente aquí es «sin enlace».
  const patientId = cita?.patient_id;
  const citaId = cita?.id;
  useEffect(() => {
    if (!open || !mensajesDeCirugia || !patientId) return undefined;
    let vigente = true;
    setPasosEstado(undefined);
    api.get('/api/cirugia-pasos/estado', { params: { patient_id: patientId, cita_id: citaId } })
      .then((r) => { if (vigente) setPasosEstado(r.data || null); })
      .catch(() => { if (vigente) setPasosEstado(null); });
    return () => { vigente = false; };
  }, [open, mensajesDeCirugia, patientId, citaId, pasosTick]);

  // Retoque en quirófano (tipo marcado como retoque en Gestión de agendas):
  // se pregunta por el coste al
  // abrir el resumen, para que el aviso se vea ANTES de pulsar Confirmación.
  const esRetoque = esQuirofano && esConceptoRetoque(cita?.concept_code, conceptos);
  useEffect(() => {
    if (!open || !esRetoque || !patientId) return undefined;
    let vigente = true;
    setCosteRetoque(undefined);
    api.get(`/api/recordatorios/cita/${citaId}/retoque-coste`)
      .then((r) => { if (vigente) setCosteRetoque(r.data || null); })
      .catch(() => { if (vigente) setCosteRetoque(null); });
    return () => { vigente = false; };
  }, [open, esRetoque, patientId, citaId]);

  if (!cita) return null;

  // `conceptos` llega CON los retirados (revisión 2026-09-03): así una
  // cirugía antigua con «Cirugía»/«Cirugía menor» conserva su color. Para
  // elegir solo se ofrecen los activos, más el que la cita ya lleve si es
  // retirado (marcado «(retirado)»), para que el desplegable no quede en blanco.
  const concepto = conceptos.find((c) => c.code === cita.concept_code);
  // La reserva en su color, como en la agenda (12/10 y 15/10/2026):
  // amarilla sin pagar, azul o verde pagada; si no, el color del tipo.
  const conReservaPendiente = reservaPendiente(cita);
  const colorReserva = colorDeLaReserva(cita.estado_reserva);
  const color = colorReserva ? colorReserva.punto : conceptColor(concepto);
  // Cómo se quita la reserva sin pagar: lo dice la configuración de su tipo
  // (los cobros que la pagan, o a mano). Sin datos de la clínica aquí.
  const ayudaReserva = (() => {
    if (!conReservaPendiente) return '';
    const normal = concepto?.name ? `«${concepto.name}»` : 'el tipo normal';
    if (reservaSePagaSola(concepto)) {
      const textos = (concepto.reserva_cobros || []).map((t) => `«${t}»`).join(' o ');
      return `Reserva sin pagar: queda pagada al apuntar en Cobros un pago de ${textos}. Si se cobró fuera, elige ${normal} en el tipo.`;
    }
    return `Reserva sin pagar: se quita a mano eligiendo ${normal} en el tipo.`;
  })();
  const elegibles = conceptosElegibles(conceptos, cita.concept_code);
  // Con la reserva ya pagada el selector no la ofrece: está cobrada, y para
  // volver a «sin pagar» se corrige el cobro en Cobros.
  const ofreceReserva = !reservaPagada(cita);
  const durNum = parseInt(durDraft, 10);
  const durValida = Number.isFinite(durNum) && durNum > 0;
  // La fila de estados del día y si es «larga» (los cinco de consulta, o más
  // con un estado anterior): entonces va con los nombres cortos y en una línea.
  const filaEstados = estadosDeLaCita(agenda, cita, estado);
  const filaLarga = filaEstados.length >= 5;

  const pickEstado = async (value) => {
    const next = estado === value ? null : value; // repetir clic = quitar estado
    setSavingEstado(true);
    try {
      const updated = await setEstadoCita(cita.id, next);
      setEstado(updated.estado_paciente || null);
      // «Visto por enfermería» (15/10/2026): en la consulta conjunta el
      // servidor pone o quita con el estado la marca de siempre, la que ve el
      // médico en su agenda. Se dice, como decía el botón de antes.
      const vistoAhora = !!updated.visto_enfermeria;
      if (consultaConjunta && vistoAhora !== visto) {
        toast.success(vistoAhora ? 'Marcado: visto por enfermería' : 'Quitado el visto por enfermería');
      }
      setVisto(vistoAhora);
      setVistoPor(updated.visto_enfermeria_by || null);
      // Cirugía o retoque marcada como «En recuperación»/«Se fue»: el backend
      // deja un episodio en la ficha del paciente (2026-09-08, punto 4). El
      // chalazión de quirófano, con «Realizado» o «No realizado» (30/9/2026),
      // y si se cambia de uno a otro lo pone al día o avisa de que no puede.
      avisosDelEpisodio(updated, cita).forEach((a) => toast[a.tipo](a.texto));
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el estado');
    } finally {
      setSavingEstado(false);
    }
  };

  // Pedir reseña de Google (2026-08-25): se elige a qué centro va la
  // reseña, cada uno con su ficha (los de Configuración: CentrosResena).
  const enviarResena = async (clinica) => {
    const ventana = abrirPestanaYa();
    setResenaOpen(false);
    setEnviandoMsg('resena');
    try {
      const { data: d } = await api.get(`/api/recordatorios/cita/${cita.id}/resena`, { params: { clinica } });
      if (!d?.whatsapp_url) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, d.whatsapp_url);
      // La reseña saluda como los demás mensajes (administración, 24/9/2026): si
      // sale «Estimado/a», el mismo aviso, sin frenar el envío.
      if (d.saludo_neutro) avisarSaludoNeutro();
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje de reseña');
    } finally {
      setEnviandoMsg(null);
    }
  };

  // Confirmación / recordatorio desde el propio resumen (2026-08-05): si el
  // aviso de al crear la cita se cerró sin enviar, desde aquí se manda igual.
  // UN SOLO mensaje (administración, 2026-09-09): el formulario del paciente con
  // documentación pendiente va dentro del propio texto, resaltado. Entre el
  // 2026-09-08 y el 2026-09-09 salía en un segundo WhatsApp que había que
  // abrir con otro botón, y ese segundo mensaje no se mandaba.
  // En quirófano el backend ya devuelve la confirmación de CIRUGÍA y, como
  // recordatorio, la guía de «mis pasos» con su enlace (puntos 6 y 8): por
  // eso aquí no hay botones aparte de cirugía (2026-09-03, dirección, punto 7).
  // Salvo en un RETOQUE (administración, 2026-09-17): ahí el recordatorio son las
  // pautas preoperatorias, sin guía y sin enlace. Aquí no cambia nada —el
  // texto lo elige el servidor—, pero sí el rótulo del botón, que si no
  // promete lo que no manda.
  const enviarMensaje = async (tipo) => {
    const ventana = abrirPestanaYa();
    setEnviandoMsg(tipo);
    try {
      const d = await mensajeCita(cita.id, tipo);
      if (!d?.whatsapp_url) {
        ventana?.close();
        toast.error('El paciente no tiene un teléfono válido para WhatsApp');
        return;
      }
      llevarPestanaA(ventana, d.whatsapp_url);
      // Saludo «Estimado/a» (cierre 2026-09-05): aviso, sin frenar el envío.
      if (d.saludo_neutro) avisarSaludoNeutro();
      // Confirmación de un retoque sin coste conocido (2026-09-08): el
      // mensaje lleva el bloque de pago; aviso para revisarlo antes de mandar.
      if (costeRetoqueDesconocido(d)) avisarCosteRetoque(cita.patient_name);
      // Igual que el panel Recordatorios (cierre 2026-09-05): se deja
      // constancia del envío en la cita; si no, el panel la seguía dando
      // por pendiente. La marca la devuelve el servidor y se pinta esa
      // (punto 14, 2026-09-08): antes se ponía la hora del navegador y, si
      // la marca fallaba, no se enteraba nadie y el panel la seguía dando
      // por pendiente aunque el mensaje hubiera salido.
      // Y se marca la casilla DE ESTE mensaje (revisión 2026-09-21): el
      // `tipo` va como parámetro de la petición, el mismo que se le acaba de
      // pedir a GET /cita/{id}. Sin él el servidor marcaba siempre el
      // recordatorio, y una confirmación mandada al dar la cita dejaba a la
      // paciente sin el aviso del día antes.
      const marca = marcaDeMensaje(tipo);
      api.post(`/api/recordatorios/${cita.id}/whatsapp-enviado`, null, { params: { tipo } }).then(({ data: m }) => {
        setEnviadoAqui((prev) => ({
          ...prev,
          [marca.tipo]: {
            at: m?.[marca.campoAt] || new Date().toISOString(),
            canal: m?.[marca.campoCanal] || 'whatsapp',
          },
        }));
        onChanged?.();
      }, (err) => {
        toast.warning('No se ha podido registrar el envío: la cita sigue como pendiente en Recordatorios', {
          description: err?.response?.data?.detail || undefined,
          duration: 12000,
        });
      });
      // El recordatorio de CIRUGÍA crea el enlace de la guía si no existía:
      // que el seguimiento pase de «sin enlace» a «0 de 6» sin reabrir. El de
      // un retoque ya no lo crea (administración, 2026-09-17); se vuelve a
      // preguntar igual, y el seguimiento seguirá diciendo «sin enlace»
      // hasta que alguien pulse «Generar sus pasos».
      if (tipo === 'recordatorio' && mensajesDeCirugia) setPasosTick((n) => n + 1);
    } catch (e) {
      ventana?.close();
      toast.error(e?.response?.data?.detail || 'No se pudo preparar el mensaje');
    } finally {
      setEnviandoMsg('');
    }
  };

  // Los detalles son obligatorios (2026-09-03, dirección, punto 5): no se
  // pueden dejar vacíos al editar.
  const faltanDetalles = !notesDraft.trim();
  const errorDetalles = faltanDetalles && notesTocado;

  const editarNotas = () => {
    setNotesTocado(false);
    setEditingNotes(true);
  };

  // LOS DETALLES QUE NOMBRAN OTRA CIRUGÍA (23/9/2026). El servidor no retira
  // consentimientos por una nota escrita a mano (puede ampliar la cirugía o
  // ser vieja, y no lo sabe); si nombra una cirugía que el tipo o el
  // procedimiento no nombran, lo dice en `aviso_documentos`. Al guardar, el
  // toast lo repite para que no pase desapercibido.
  const avisarDocumentos = (updated) => {
    if (updated?.aviso_documentos) toast.warning(updated.aviso_documentos, { duration: 12000 });
  };

  const saveNotes = async () => {
    if (faltanDetalles) {
      setNotesTocado(true);
      toast.error('Los detalles de la cita son obligatorios');
      return;
    }
    setSavingNotes(true);
    try {
      const updated = await updateCita(cita.id, { notes: notesDraft.trim() });
      toast.success('Detalles de cita guardados');
      avisarDocumentos(updated);
      setEditingNotes(false);
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar los detalles');
    } finally {
      setSavingNotes(false);
    }
  };

  // Cambiar el TIPO de cita (concepto). Al abrir se parte del tipo y la
  // duración actuales; al elegir otro tipo la duración se rellena con la de
  // ese tipo en esta agenda (override o por defecto) y se puede corregir a
  // mano antes de guardar. force:true → el backend lo respeta solo para quien
  // puede ajustar el hueco; si el nuevo tipo dura más y se solapa, se apila
  // (la agenda ya no estrecha lateralmente).
  const startEditConcept = () => {
    setConceptDraft(cita.concept_code || '');
    setReservaDraft(reservaPendiente(cita));
    setDurDraft(cita.duration_minutes ? String(cita.duration_minutes) : '');
    setEditingConcept(true);
  };
  const pickConcept = (valor) => {
    const { code, reserva } = leerTipoCita(valor);
    if (!code) return;
    setReservaDraft(reserva);
    // «Reserva X» y «X» son el mismo tipo: la duración escrita se queda.
    if (code === conceptDraft) return;
    setConceptDraft(code);
    const minutos = minutosConcepto(agenda, conceptos.find((c) => c.code === code));
    setDurDraft(minutos ? String(minutos) : '');
  };
  const saveConcept = async () => {
    const cambiaTipo = conceptDraft && conceptDraft !== cita.concept_code;
    const cambiaReserva = reservaDraft !== reservaPendiente(cita);
    const cambiaDuracion = durValida && durNum !== Number(cita.duration_minutes || 0);
    if (!cambiaTipo && !cambiaDuracion && !cambiaReserva) { setEditingConcept(false); return; }
    if (!durValida) {
      toast.error('La duración tiene que ser un número de minutos mayor que cero');
      return;
    }
    setSavingConcept(true);
    try {
      const payload = { duration_minutes: durNum, force: true };
      if (cambiaTipo) payload.concept_code = conceptDraft;
      if (cambiaReserva) payload.reserva = reservaDraft;
      const updated = await updateCita(cita.id, payload);
      toast.success(cambiaTipo ? 'Tipo de cita actualizado'
        : cambiaReserva ? (reservaDraft ? 'Cita marcada como reserva sin pagar' : 'Reserva quitada a mano: la cita queda en su tipo normal')
          : 'Duración actualizada');
      avisarDocumentos(updated);
      setEditingConcept(false);
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el tipo de cita');
    } finally {
      setSavingConcept(false);
    }
  };

  // Cambiar el PACIENTE de la cita: la secretaria se equivoca al darla, o la
  // cita se le cede a otro. Antes había que cancelarla y volver a crearla.
  const pickPatient = async (patientId, excepcionProhibido = false) => {
    if (!patientId || patientId === cita.patient_id) { setEditingPatient(false); return; }
    setSavingPatient(true);
    try {
      const updated = await updateCita(cita.id, {
        patient_id: patientId, force: true,
        ...(excepcionProhibido === true ? { excepcion_prohibido: true } : {}),
      });
      toast.success(cita.patient_id ? 'Paciente cambiado' : 'Ficha del paciente puesta en la cita');
      avisarDocumentos(updated);
      setEditingPatient(false);
      onChanged?.(updated);
    } catch (e) {
      // Pasarle la cita a un paciente PROHIBIDO (14/10/2026): el aviso
      // emergente, sin el motivo; admin y dirección, con la excepción.
      if (esErrorProhibido(e)) setProhibidoPendiente(patientId);
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el paciente');
    } finally {
      setSavingPatient(false);
    }
  };

  const saveProc = async () => {
    setSavingProc(true);
    try {
      const updated = await updateCita(cita.id, { procedimiento: procDraft.trim() });
      toast.success('Procedimiento guardado');
      avisarDocumentos(updated);
      setEditingProc(false);
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el procedimiento');
    } finally {
      setSavingProc(false);
    }
  };

  const doNoAcude = async () => {
    if (!window.confirm('¿Marcar que el paciente NO ACUDIÓ a esta cita? Quedará en su histórico.')) return;
    try {
      const updated = await marcarNoAcude(cita.id);
      toast.success('Cita marcada como «No acude»: se queda en la agenda con el nombre tachado');
      onOpenChange(false);
      // Con la cita devuelta, la rejilla la tacha en el sitio sin esperar a la recarga.
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar la cita');
    }
  };

  // Marcha atrás de un «no acude» marcado por error: la cita vuelve a activa
  // y se destacha en la rejilla (dirección, 2026-09-08, punto 12).
  const doDeshacerNoAcude = async () => {
    if (deshaciendoNoAcude) return;
    setDeshaciendoNoAcude(true);
    try {
      const updated = await deshacerNoAcude(cita.id);
      toast.success('«No acude» deshecho: la cita vuelve a estar activa');
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo deshacer');
    } finally {
      setDeshaciendoNoAcude(false);
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      await cancelCita(cita.id, cancelReason.trim());
      toast.success('Cita cancelada');
      setCancelOpen(false);
      onOpenChange(false);
      onChanged?.();
      onCancelada?.(cita);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar la cita');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="cita-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
              {cita.patient_id ? (
                <Link
                  to={`/pacientes/${cita.patient_id}`}
                  target="_blank"
                  rel="noopener"
                  className="truncate hover:underline text-[#13231E]"
                  data-testid="cita-detail-patient-link"
                >
                  {cita.patient_name}
                </Link>
              ) : (
                <span className="truncate">{cita.patient_name}</span>
              )}
              <IndicadorProhibido
                prohibido={cita.prohibido}
                motivo={cita.prohibido_motivo}
                tamano="pequeno"
                testId="cita-detail-prohibido"
              />
            </DialogTitle>
            {/* Sus intervenciones ya realizadas, TODAS (14/10/2026): en
                cualquier cita del paciente, también en las futuras. Aquí hay
                sitio y van enteras; si no caben en una línea, pasan a la
                siguiente. Las calcula el servidor (`cirugias_previas`). */}
            {/* Y sus MARCAS DE PACIENTE (administración, 21/10/2026): la
                misma etiqueta, en el color de la marca, con el texto que
                guarda la cita. Ya no van como línea en las notas de
                administración. */}
            {(ordenarIntervenciones(cita.cirugias_previas).length > 0 || marcasDeLaCita(cita.marcas_paciente).length > 0) && (
              <div className="flex flex-wrap items-center gap-1 min-w-0" data-testid="cita-detail-etiquetas">
                <EtiquetasIntervenciones
                  intervenciones={cita.cirugias_previas}
                  testId="cita-detail-intervenciones"
                />
                <EtiquetasMarcasPaciente marcas={cita.marcas_paciente} testId="cita-detail-marcas" />
              </div>
            )}
          </DialogHeader>

          {/* `min-w-0` (15/10/2026): el diálogo es una rejilla, y sin él la fila
              de estados (en una línea, sin saltos) ensanchaba la columna en un
              resumen estrecho y salía una barra lateral; con él, la fila encoge. */}
          <div className="min-w-0 space-y-2.5 text-sm">
            {/* La misma paciente citada a la vez en otro sitio (23/9/2026,
                `duplicada_en` del servidor): lo primero que se ve. */}
            <CitaDuplicadaAviso duplicada={cita.duplicada_en} nombre={cita.patient_name} testId="cita-detail-duplicada" />
            {/* Cambiar de paciente sin cancelar la cita (2026-07-28). */}
            {editingPatient ? (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                <div className="flex-1 min-w-0">
                  <PatientCombobox
                    value={cita.patient_id || ''}
                    onChange={(id) => pickPatient(id)}
                    disabled={savingPatient}
                    dataTestid="cita-detail-patient-select"
                  />
                </div>
                {/* Si no está en la lista, se da de alta aquí mismo y queda
                    puesto en la cita, sin salir del resumen. */}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0 h-8 px-2"
                  disabled={savingPatient}
                  onClick={() => setNewPatientOpen(true)}
                  title="Crear un contacto nuevo y ponerlo en esta cita"
                  data-testid="cita-detail-patient-new"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Nuevo
                </Button>
                <button
                  type="button"
                  onClick={() => setEditingPatient(false)}
                  className="text-muted-foreground hover:text-[#13231E] shrink-0"
                  title="Dejarlo como estaba"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingPatient(true)}
                className="flex items-center gap-2 text-muted-foreground hover:text-[#2E6E5E]"
                data-testid="cita-detail-patient-edit"
              >
                <User className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                {/* Sin ficha (citas traídas de la agenda o del Agente) lo que
                    hace falta no es «cambiar», es ponerle una o crearla. */}
                <span className="text-xs">{cita.patient_id ? 'Cambiar de paciente' : 'Poner o crear la ficha del paciente'}</span>
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {cita.patient_phone && (
              <div className="flex items-center gap-2 text-[#13231E]">
                <Phone className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                {/* El texto se lee con espacios; el enlace, no: «tel:+44 7700
                    900123» con espacios no marca en todos los teléfonos. */}
                <a href={`tel:${telefonoParaEnlace(cita.patient_phone)}`} className="tabular-nums hover:underline">
                  {telefonoConPrefijo(cita.patient_phone)}
                </a>
              </div>
            )}
            <div className="flex items-start gap-2 text-[#13231E]">
              <Clock className="w-4 h-4 text-[#2E6E5E] shrink-0 mt-2" />
              {editingConcept ? (
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Select value={valorTipoCita(conceptDraft, reservaDraft)} onValueChange={pickConcept} disabled={savingConcept}>
                    <SelectTrigger className="h-8" data-testid="cita-detail-concept-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* El mismo orden que el alta y «Dar cita»: consultas,
                          cirugías y al final todas las «Reserva …»
                          (administración, 21/10/2026; `opcionesTipoCita`). */}
                      {opcionesTipoCita(elegibles, {
                        conReserva: ofreceReserva,
                        ofreceReserva: (c) => conceptoAdmiteReserva(c) && c.active !== false,
                      }).map(({ concepto: c, reserva }) => (reserva ? (
                        <SelectItem key={valorTipoCita(c.code, true)} value={valorTipoCita(c.code, true)}>
                          {nombreReserva(c.name)}
                        </SelectItem>
                      ) : (
                        <SelectItem key={c.code} value={c.code}>{nombreConcepto(c)}</SelectItem>
                      )))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="cita-detail-dur" className="text-xs text-muted-foreground whitespace-nowrap">Duración (min)</Label>
                    <Input
                      id="cita-detail-dur"
                      type="number"
                      min={5}
                      step={5}
                      value={durDraft}
                      onChange={(e) => setDurDraft(e.target.value)}
                      disabled={savingConcept}
                      className="h-8 w-24 tabular-nums"
                      data-testid="cita-detail-duration"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingConcept(false)} disabled={savingConcept}>
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={saveConcept}
                      disabled={savingConcept || !durValida}
                      className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                      data-testid="cita-detail-concept-save"
                    >
                      {savingConcept && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="mt-1.5" data-testid="cita-detail-concept-name">
                    {nombreTipoDeLaCita(cita)}
                    {cita.duration_minutes ? ` · ${cita.duration_minutes} min` : ''}
                    {/* La reserva pagada (en azul o en verde) o quitada a
                        mano: lo que dice su color, con palabras (15/10/2026;
                        cuánto falta, solo a gestión: lo filtra el servidor). */}
                    {[ESTADO_RESERVA.PAGADA_PARCIAL, ESTADO_RESERVA.PAGADA_VERDE, ESTADO_RESERVA.CONFIRMADA]
                      .includes(cita.estado_reserva) && (
                      <span className="block text-xs text-muted-foreground" data-testid="cita-detail-reserva-estado">
                        ▸ {cita.estado_reserva_titulo || (cita.estado_reserva === ESTADO_RESERVA.PAGADA_PARCIAL
                          ? 'Pendiente de pago' : cita.estado_reserva === ESTADO_RESERVA.PAGADA_VERDE
                            ? 'Pagada' : 'Reserva quitada a mano')}
                      </span>
                    )}
                    {conReservaPendiente && (
                      <span className="block text-xs text-muted-foreground" data-testid="cita-detail-reserva-ayuda">
                        {ayudaReserva}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={startEditConcept}
                    className="text-muted-foreground hover:text-[#2E6E5E] mt-1.5"
                    title="Cambiar tipo de cita o duración"
                    data-testid="cita-detail-concept-edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            {/* Fecha y hora: se cambian en "Mover cita", que ya valida huecos. */}
            <div className="flex items-center gap-2 text-[#13231E]">
              <CalendarClock className="w-4 h-4 text-[#2E6E5E] shrink-0" />
              <span className="tabular-nums">
                {formatDate(cita.date)} · {cita.start}
                {cita.end ? `–${cita.end}` : ''}
              </span>
              <button
                type="button"
                onClick={() => setMoveOpen(true)}
                className="text-muted-foreground hover:text-[#2E6E5E]"
                title="Cambiar fecha u hora"
                data-testid="cita-detail-fecha-edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            {cita.lugar && (
              <div className="flex items-center gap-2 text-[#13231E]">
                <MapPin className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                <span>{cita.lugar}</span>
              </div>
            )}
            {cita.cirujano && (
              <div className="flex items-center gap-2 text-[#13231E]">
                <Stethoscope className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                <span>Cirujano: {cita.cirujano}</span>
              </div>
            )}
            {/* Procedimiento: editable aquí mismo (2026-07-28). */}
            {editingProc ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Syringe className="w-4 h-4 text-[#2E6E5E] shrink-0" /> Procedimiento
                </div>
                <Textarea
                  rows={2}
                  value={procDraft}
                  onChange={(e) => setProcDraft(e.target.value)}
                  disabled={savingProc}
                  data-testid="cita-detail-proc-input"
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setEditingProc(false)} disabled={savingProc}>
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={saveProc} disabled={savingProc} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="cita-detail-proc-save">
                    {savingProc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
            ) : cita.procedimiento ? (
              <div className="flex items-start gap-2 text-[#13231E]">
                <Syringe className="w-4 h-4 text-[#2E6E5E] shrink-0 mt-0.5" />
                <span className="flex-1 min-w-0">
                  {cita.procedimiento}
                </span>
                <button
                  type="button"
                  onClick={() => setEditingProc(true)}
                  className="text-muted-foreground hover:text-[#2E6E5E] shrink-0 mt-0.5"
                  title="Editar procedimiento"
                  data-testid="cita-detail-proc-edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : null}
            {/* Nota de cita (qué se hace: "valorar blefaroplastia"…) editable */}
            {editingNotes ? (
              <div className="space-y-1.5">
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  onBlur={() => setNotesTocado(true)}
                  placeholder="Ej.: Valorar blefaroplastia, revisión de ptosis…"
                  rows={2}
                  className={errorDetalles ? 'border-red-300' : ''}
                  aria-invalid={errorDetalles}
                  data-testid="cita-notes-input"
                />
                {errorDetalles ? (
                  <div className="text-xs text-red-600" data-testid="cita-notes-error">
                    Los detalles de la cita son obligatorios
                  </div>
                ) : faltanDetalles && (
                  <div className="text-xs text-muted-foreground" data-testid="cita-notes-ayuda">
                    Obligatorio
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingNotes(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={saveNotes}
                    disabled={savingNotes || faltanDetalles}
                    className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    data-testid="cita-notes-save"
                  >
                    {savingNotes && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                    Guardar detalles
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-muted-foreground">
                <StickyNote className="w-4 h-4 text-[#2E6E5E] shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap flex-1">
                  {cita.notes || (
                    // Citas de antes de que los detalles fueran obligatorios
                    // (punto 5): aviso suave, sin bloquear nada, con el
                    // atajo al editor (revisión 2026-09-03).
                    <span className="text-amber-700" data-testid="cita-notes-aviso">
                      Sin detalles de cita —{' '}
                      <button type="button" onClick={editarNotas} className="underline underline-offset-2 hover:text-amber-900">
                        añádelos
                      </button>
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={editarNotas}
                  className="text-[#2E6E5E] hover:text-[#13231E] shrink-0"
                  title="Editar detalles de cita"
                  data-testid="cita-notes-edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Detalles que nombran una cirugía que el tipo o el
                procedimiento no nombran (23/9/2026): los consentimientos
                siguen la nota, y alguien tiene que mirar si vale. */}
            {cita.aviso_documentos && (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
                data-testid="cita-aviso-documentos"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                <span className="flex-1">
                  {cita.aviso_documentos}{' '}
                  {!editingNotes && (
                    <button
                      type="button"
                      onClick={editarNotas}
                      className="underline underline-offset-2 hover:text-amber-950"
                      data-testid="cita-aviso-documentos-editar"
                    >
                      Editar los Detalles
                    </button>
                  )}
                </span>
              </div>
            )}

            {/* Alergias, medicación, ácido hialurónico previo y antecedentes
                médicos de la ficha, junto a los detalles (2026-09-03, dirección,
                punto 5; sin «Enfermedades» desde el punto 9, 2026-09-08).
                Solo lectura. */}
            <InfoClinicaPaciente patientId={cita.patient_id} testId="cita-detail-info-clinica" />

            {/* Notas de administración (administración, 18/9/2026): debajo de la
                información clínica y ENCIMA del flujo del día. Quien no pueda
                verlas no ve nada — lo decide el 403 del servidor. */}
            <NotasAdminCita patientId={cita.patient_id} puedeVerlas={puedeVerNotasAdmin} />

            {/* Flujo del día. En consulta son cinco pasos desde el 15/10/2026
                (sala de espera → enfermería → visto por enfermería → consulta →
                realizada); "listo para cambiarse", "en recuperación" y "se fue"
                solo tienen sentido en quirófano (2026-07-28). El chalazión citado
                en quirófano lleva "listo para cambiarse", "realizado" y "no
                realizado" (30/9/2026): lo dice el servidor en la cita, como dice
                que una revisión citada en quirófano lleva los de consulta
                (2/10/2026). Si guarda un estado de antes que ya no se ofrece
                («se fue»), se ve aparte y un clic lo quita.
                TODOS EN UNA LÍNEA (administración, 15/10/2026): con cinco o más,
                cada botón dice su nombre corto («Espera», «Visto enf.») con el
                entero en el `title`, mide lo que su texto (el hueco que sobra se
                reparte) y, si aun así no cabe (un resumen muy estrecho), encoge
                con «…» en vez de saltar de línea o sacar una barra lateral.
                Medido en Chromium con la letra del sistema: caben enteros desde
                un móvil de 360 px. */}
            {!noAcude && (
            <div
              className={`flex flex-nowrap min-w-0 ${filaLarga ? 'gap-1' : 'gap-1.5'}`}
              data-testid="cita-estado-chips"
              data-larga={filaLarga ? '1' : undefined}
            >
              {filaEstados.map((e) => {
                const active = estado === e.value;
                const texto = filaLarga ? (e.corto || e.label) : e.label;
                return (
                  <button
                    key={e.value}
                    type="button"
                    onClick={() => pickEstado(e.value)}
                    disabled={savingEstado}
                    data-testid={`cita-estado-${e.value}`}
                    data-anterior={e.anterior ? '1' : undefined}
                    aria-label={texto !== e.label ? e.label : undefined}
                    aria-pressed={active}
                    title={e.anterior
                      ? `${e.label}: estado marcado antes del cambio de botones, púlsalo para quitarlo`
                      : (filaLarga ? e.label : undefined)}
                    className={`rounded-md border py-1.5 font-medium transition-colors ${
                      filaLarga
                        ? 'flex-auto min-w-0 truncate px-0.5 text-[11px] leading-4'
                        : 'flex-1 px-2 text-xs'
                    } ${
                      active ? 'text-white' : 'bg-white text-muted-foreground hover:text-[#13231E]'
                    }`}
                    style={active
                      ? { backgroundColor: e.color, borderColor: e.color }
                      : { borderColor: 'hsl(var(--border))' }}
                  >
                    {texto}
                  </button>
                );
              })}
            </div>
            )}

            {/* Consulta conjunta: quién marcó el visto de enfermería. Se marca y
                se quita con «Visto enf.» en la fila de arriba (15/10/2026); esto
                solo dice quién, y sigue a la vista cuando el paciente ya ha
                pasado a consulta. */}
            {consultaConjunta && !noAcude && visto && (
              <p
                className="-mt-1 flex items-center gap-1.5 text-[11px] text-emerald-800"
                data-testid="cita-visto-info"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  Visto por enfermería{vistoPor ? ` · ${vistoPor}` : ''}
                </span>
              </p>
            )}

            {/* Quirófano: cómo va el paciente con la guía de «mis pasos». Es un
                estado de la cita, no una acción más: se pulsa y se abre el
                seguimiento (antes botón «Guía de mis pasos», 2026-09-03, punto 7). */}
            {mensajesDeCirugia && cita.patient_id && (
              <button
                type="button"
                onClick={() => setPasosOpen(true)}
                data-testid="cita-detail-pasos-estado"
                title="Ver el seguimiento de la guía: qué apartados ha completado el paciente"
                className="flex w-full items-center gap-2 rounded-md border border-[#D9E3DD] bg-white px-2.5 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-[#284264] hover:text-[#13231E]"
              >
                <ListChecks className="w-4 h-4 text-[#284264] shrink-0" />
                <span className="flex-1">Guía de mis pasos</span>
                <PasosResumen estado={pasosEstado} />
              </button>
            )}
            {esRetoque && cita.patient_id && <RetoqueCoste coste={costeRetoque} />}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1" data-testid="cita-detail-acciones">
            {cita.patient_id && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => enviarMensaje('confirmacion')}
                  disabled={!!enviandoMsg}
                  data-testid="cita-detail-confirmacion"
                  title="Mandarle la confirmación de su cita"
                >
                  {enviandoMsg === 'confirmacion'
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                  Confirmación
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => enviarMensaje('recordatorio')}
                  disabled={!!enviandoMsg}
                  data-testid="cita-detail-recordatorio"
                  // El rótulo dice lo que DE VERDAD sale. Desde el cambio de
                  // Administración (2026-09-17) el recordatorio de un RETOQUE manda
                  // las pautas preoperatorias y ya no manda la guía de «mis
                  // pasos» ni su enlace; con el texto de la cirugía aquí,
                  // recepción pulsaba creyendo que mandaba una cosa y mandaba
                  // otra. El de una cirugía nueva no cambia: ese sí lleva guía.
                  title={esRetoque
                    ? 'Mandarle el recordatorio de su retoque: las pautas preoperatorias (sin la guía de mis pasos)'
                    : mensajesDeCirugia
                      ? 'Mandarle el recordatorio de su cirugía: la guía de mis pasos con su enlace'
                      : 'Mandarle el recordatorio de su cita'}
                >
                  {enviandoMsg === 'recordatorio'
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <MessageCircle className="w-4 h-4 mr-1.5" />}
                  Recordatorio
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="col-span-2 border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                  onClick={() => setResenaOpen(true)}
                  disabled={!!enviandoMsg}
                  data-testid="cita-detail-resena"
                  title="Pedirle una reseña de Google (se elige la clínica)"
                >
                  {enviandoMsg === 'resena'
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <Star className="w-4 h-4 mr-1.5" />}
                  Pedir reseña
                </Button>
              </>
            )}
            {/* Dar la SIGUIENTE cita al paciente sin salir del popover */}
            {cita.patient_id && (
              <Button
                type="button"
                onClick={() => setDarCitaOpen(true)}
                className="col-span-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="cita-detail-dar-cita"
              >
                <CalendarPlus className="w-4 h-4 mr-1.5" /> Dar cita
              </Button>
            )}
            {!noAcude && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setMoveOpen(true)}
                data-testid="cita-detail-move"
              >
                <MoveRight className="w-4 h-4 mr-1.5" /> Mover cita
              </Button>
            )}
            {noAcude ? (
              <Button
                type="button"
                variant="outline"
                onClick={doDeshacerNoAcude}
                disabled={deshaciendoNoAcude}
                className="border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                title="Marcado por error: la cita vuelve a estar activa"
                data-testid="cita-detail-deshacer-no-acude"
              >
                {deshaciendoNoAcude
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <RotateCcw className="w-4 h-4 mr-1.5" />}
                Deshacer «No acude»
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={doNoAcude}
                className="border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                data-testid="cita-detail-no-acude"
              >
                <UserX className="w-4 h-4 mr-1.5" /> No acude
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelOpen(true)}
              className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="cita-detail-cancel"
            >
              <CalendarX2 className="w-4 h-4 mr-1.5" /> Cancelar cita
            </Button>
            {/* En quirófano ya no va (2026-09-03, dirección, punto 7): el nombre
                de la cabecera abre la ficha igual. En consulta se deja, y en
                una consulta citada en quirófano también (2/10/2026). */}
            {cita.patient_id && (!esQuirofano || consultaEnQuirofano) && (
              <Button asChild className="col-span-2 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="cita-detail-view-patient">
                <Link to={`/pacientes/${cita.patient_id}`} target="_blank" rel="noopener">
                  <User className="w-4 h-4 mr-1.5" /> Abrir historia clínica
                  <ExternalLink className="w-3.5 h-3.5 ml-1.5 opacity-60" />
                </Link>
              </Button>
            )}
          </div>

          {cita.created_by_name && (
            <div className="text-[11px] text-muted-foreground pt-1" data-testid="cita-detail-created-by">
              Creada por {cita.created_by_name}
              {admin && cita.created_at ? ` · ${formatFechaHora(cita.created_at)}` : ''}
            </div>
          )}
          {/* Lo que ya se le ha mandado, UNA LÍNEA POR MENSAJE (revisión
              2026-09-21; antes una sola, «Recordatorio enviado el …», para los
              dos). Aquí igual que en el panel del día: dice QUÉ mensaje salió,
              CUÁNDO y por dónde, con la marca del servidor y no con la hora
              del navegador (cierre 2026-09-05; punto 14, 2026-09-08). La que
              no ha salido no se anuncia: su silencio es el dato. */}
          {MARCAS_MENSAJE.map((m) => {
            const at = enviadoAqui[m.tipo]?.at || cita[m.campoAt];
            if (!at) return null;
            const canal = enviadoAqui[m.tipo]?.canal || cita[m.campoCanal];
            return (
              <div key={m.tipo} className="text-[11px] text-muted-foreground" data-testid={m.testid}>
                {m.rotulo} el {formatFechaHora(at)}{canal ? ` por ${nombreCanal(canal)}` : ''}
              </div>
            );
          })}
          {/* Confirmada por el paciente pulsando el enlace del recordatorio
              (2026-09-03): en la agenda, etiqueta y borde verdes (desde el 4/10/2026
              el fondo es el de su tipo); aquí, la hora en que lo hizo. */}
          {cita.paciente_confirmacion === 'confirmada' && (
            <div className="text-[11px] text-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded px-2 py-1.5" data-testid="cita-detail-confirmada-paciente">
              {/* Por dónde, que no siempre es el enlace (30/9/2026): la
                  respuesta por WhatsApp también confirma. */}
              {cita.paciente_confirmacion_via === 'whatsapp'
                ? '✓ Confirmada por el paciente respondiendo por WhatsApp'
                : '✓ Confirmada por el paciente desde el enlace del recordatorio'}
              {cita.paciente_confirmacion_at ? ` · ${formatFechaHora(cita.paciente_confirmacion_at)}` : ''}
            </div>
          )}
          {cita.status === 'no_acude' && (
            <div className="text-[11px] text-amber-900 bg-amber-50 dark:bg-amber-950/30 rounded px-2 py-1.5" data-testid="cita-detail-no-acude-info">
              No acudió{cita.no_acude_by_name ? ` · marcado por ${cita.no_acude_by_name}` : ''}
              {cita.no_acude_at ? ` · ${formatFechaHora(cita.no_acude_at)}` : ''}
            </div>
          )}
          {/* Trazabilidad de la anulación: solo administradores. */}
          {admin && cita.status === 'cancelada' && (
            <div className="text-[11px] text-red-700 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1.5" data-testid="cita-detail-cancelled-by">
              Anulada{cita.cancelled_by_name ? ` por ${cita.cancelled_by_name}` : ''}
              {cita.cancelled_at ? ` · ${formatFechaHora(cita.cancelled_at)}` : ''}
              {cita.cancel_reason ? ` — ${cita.cancel_reason}` : ''}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MoveCitaDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        cita={cita}
        onMoved={(updated) => {
          // Ya no se cierra el resumen: la cita se refresca en el sitio y se ve
          // la fecha nueva ahí mismo (2026-07-28).
          setMoveOpen(false);
          onChanged?.(updated);
        }}
      />

      {/* Al cerrarlo se recarga el estado: desde dentro se puede generar el enlace. */}
      <PasosCirugiaDialog
        open={pasosOpen}
        onOpenChange={(v) => { setPasosOpen(v); if (!v) setPasosTick((n) => n + 1); }}
        cita={cita}
      />

      {/* ¿A qué clínica va la reseña? (2026-08-25, dirección: se puede elegir) */}
      <Dialog open={resenaOpen} onOpenChange={setResenaOpen}>
        <DialogContent className="max-w-sm" data-testid="resena-clinica-dialog">
          <DialogHeader>
            <DialogTitle>Pedir reseña de Google</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            ¿En qué ficha de Google quieres que deje la reseña {cita.patient_name}?
          </p>
          <CentrosResena onElegir={(clave) => enviarResena(clave)} prefijo="resena" />
        </DialogContent>
      </Dialog>

      {/* Alta de contacto desde el resumen de la cita: se abre con el nombre
          que ya trae la cita repartido en nombre y apellidos, y al guardar
          queda puesto en esta cita. */}
      <PatientFormDialog
        open={newPatientOpen}
        onOpenChange={setNewPatientOpen}
        initial={nuevoContactoInicial}
        onSaved={(p) => {
          setNewPatientOpen(false);
          if (p?.id) pickPatient(p.id);
        }}
      />

      <DarCitaDialog
        open={darCitaOpen}
        onOpenChange={setDarCitaOpen}
        patient={{ id: cita.patient_id, name: cita.patient_name, prohibido: cita.prohibido }}
        defaultAgendaId={cita.agenda_id}
        onCreated={() => {
          setDarCitaOpen(false);
          onChanged?.();
        }}
      />

      <DialogoPacienteProhibido
        open={!!prohibidoPendiente}
        onOpenChange={(abierto) => { if (!abierto) setProhibidoPendiente(null); }}
        puedeExcepcion={puedeExcepcionProhibido(user)}
        onExcepcion={() => pickPatient(prohibidoPendiente, true)}
        testId="cita-detail-aviso-prohibido"
      />

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cita-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta cita?</AlertDialogTitle>
            <AlertDialogDescription>
              Se cancelará la cita de <strong>{cita.patient_name}</strong> del {formatDate(cita.date)} a las {cita.start}. El hueco quedará libre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label>Motivo (opcional)</Label>
            <Input
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ej.: El paciente no puede venir"
              className="mt-1.5"
              data-testid="cita-cancel-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cita-cancel-back">Volver</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doCancel();
              }}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cita-cancel-do"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancelar cita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
